import React, { useEffect, useRef } from "react";

import { Button } from "./button";

interface LoginPromptModalDiscoveryProps {
	open: boolean;
	onClose: () => void;
	onLogin: () => void;
}

const LoginPromptModalDiscovery: React.FC<LoginPromptModalDiscoveryProps> = ({ open, onClose, onLogin }) => {
	const modalRef = useRef<HTMLDivElement>(null);

	// Lock scroll and set aria/inert on background when modal is open
	// IMPORTANT: For this to work, wrap your main app content (excluding modals) in <div id="app-content">...</div>
	useEffect(() => {
		if (open) {
			// Lock scroll
			const originalOverflow = document.body.style.overflow;
			document.body.style.overflow = "hidden";
			// Set aria-hidden on #app-content only (not modal)
			const appContent = document.getElementById("app-content");
			if (appContent) {
				appContent.setAttribute("aria-hidden", "true");
			}
			// Focus modal
			if (modalRef.current) {
				modalRef.current.focus();
			}
			// Focus trap
			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.key === "Tab" && modalRef.current) {
					const focusable = modalRef.current.querySelectorAll<HTMLElement>(
						"button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"
					);
					const first = focusable[0];
					const last = focusable[focusable.length - 1];
					if (!focusable.length)
						return;
					if (e.shiftKey) {
						if (document.activeElement === first) {
							e.preventDefault();
							last.focus();
						}
					}
					else {
						if (document.activeElement === last) {
							e.preventDefault();
							first.focus();
						}
					}
				}
			};
			document.addEventListener("keydown", handleKeyDown);
			return () => {
				document.body.style.overflow = originalOverflow;
				if (appContent) {
					appContent.removeAttribute("aria-hidden");
				}
				document.removeEventListener("keydown", handleKeyDown);
			};
		}
	}, [open]);

	if (!open)
		return null;

	return (
		<div
			ref={modalRef}
			className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn"
			onClick={onClose}
			tabIndex={-1}
			onKeyDown={(e) => {
				if (e.key === "Escape") {
					onClose();
				}
			}}
		>
			<div
				className="relative bg-white shadow-2xl border border-[#ececff] animate-scaleIn text-center p-4 rounded-2xl w-full max-w-xs sm:p-6 sm:rounded-3xl sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl"
				onClick={e => e.stopPropagation()}
				style={{ boxShadow: "0 8px 32px rgba(63,46,200,0.12)", margin: "12vh 0", minHeight: "auto", maxHeight: "76vh", overflowY: "auto" }}
			>
				{/* Close button */}
				<button
					className="rounded-full p-1 transition-colors duration-150 hover:bg-gray-200"
					style={{ position: "absolute", top: 12, right: 16, lineHeight: 1, border: "none", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}
					onClick={onClose}
					aria-label="Close"
				>
					<img
						src="/assets/icons/close.svg"
						alt="Close"
						style={{ width: 24, height: 24, display: "block" }}
					/>
				</button>
				{/* Icon */}
				<div className="flex justify-center mb-4">
					<div className="bg-[#3F2EC8] bg-opacity-10 rounded-full p-3 flex items-center justify-center">
						<img
							src="/assets/icons/profile.svg"
							alt="Profile"
							style={{
								width: 36,
								height: 36,
								display: "block",
								filter: "brightness(0.5) drop-shadow(0 2px 6px rgba(63,46,200,0.18))",
								borderRadius: "50%"
							}}
						/>
					</div>
				</div>
				<h2 className="text-2xl font-bold text-[#3F2EC8] mb-2">Authentication Required</h2>
				<p className="mb-6 text-gray-700 text-base">Please sign in to continue.</p>
				<div className="flex flex-col gap-3 items-center">
					<Button
						className="w-full"
						onClick={onLogin}
					>
						Go to Login
					</Button>
				</div>
			</div>
		</div>
	);
};

export default LoginPromptModalDiscovery;
